// Date range filters of the Applications table (BSCPF-6999). Ranges are kept as local
// calendar days (YYYY-MM-DD) in the URL; timestamp columns are sent to the API as the
// viewer's local day boundaries, so "today" means the viewer's today.

export type DateFilterKey =
  | "submitted_at"
  | "started_at"
  | "updated_at"
  | "desired_move_in_date";

export type DateRange = { from?: string; to?: string };

export type DateRanges = Partial<Record<DateFilterKey, DateRange>>;

export const DATE_FILTER_KEYS: DateFilterKey[] = [
  "submitted_at",
  "started_at",
  "updated_at",
  "desired_move_in_date"
];

export const DATE_FILTER_LABELS: Record<DateFilterKey, string> = {
  submitted_at: "Submitted",
  started_at: "Started",
  updated_at: "Updated",
  desired_move_in_date: "Move-in"
};

// desired_move_in_date is a calendar date column; the others are timestamps.
const DATE_ONLY_KEYS: DateFilterKey[] = ["desired_move_in_date"];

export const isDateRangeInverted = (range: DateRange) =>
  Boolean(range.from && range.to && range.from > range.to);

const dateRangeUrlKeys = (key: DateFilterKey) =>
  [`${key}_from`, `${key}_to`] as const;

const toLocalYmd = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const shiftDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

type DateRangePresetOption = {
  value: string;
  label: string;
  getRange: (today: Date) => Required<DateRange>;
};

const pastPreset = (days: number): DateRangePresetOption => ({
  value: `last_${days}_days`,
  label: `Last ${days} days`,
  getRange: (today) => ({
    from: toLocalYmd(shiftDays(today, -(days - 1))),
    to: toLocalYmd(today)
  })
});

const nextPreset = (days: number): DateRangePresetOption => ({
  value: `next_${days}_days`,
  label: `Next ${days} days`,
  getRange: (today) => ({
    from: toLocalYmd(today),
    to: toLocalYmd(shiftDays(today, days - 1))
  })
});

// Move-in dates mostly lie ahead; the other columns record things that already happened.
export const getDateRangePresets = (
  key: DateFilterKey
): DateRangePresetOption[] =>
  key === "desired_move_in_date"
    ? [nextPreset(7), nextPreset(30), nextPreset(90)]
    : [pastPreset(7), pastPreset(30), pastPreset(90)];

// A real calendar day in YYYY-MM-DD: the shape alone lets 2026-13-01 or 2026-02-30 through, and
// those make `toISOString()` throw while the table renders.
const isYmd = (value?: string | null): value is string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && toLocalYmd(date) === value;
};

// URL values are user-editable (shared, bookmarked, restored from the session): keep only real
// dates, and drop a reversed range rather than send a filter that can match nothing.
export const parseDateRangesFromParams = (
  params: Record<string, string>
): DateRanges | undefined => {
  const ranges: DateRanges = {};
  DATE_FILTER_KEYS.forEach((key) => {
    const [fromKey, toKey] = dateRangeUrlKeys(key);
    const from = isYmd(params[fromKey]) ? params[fromKey] : undefined;
    const to = isYmd(params[toKey]) ? params[toKey] : undefined;
    if ((from || to) && !isDateRangeInverted({ from, to }))
      ranges[key] = { from, to };
  });
  return Object.keys(ranges).length ? ranges : undefined;
};

export const serializeDateRanges = (ranges?: DateRanges) => {
  const params: Record<string, string> = {};
  DATE_FILTER_KEYS.forEach((key) => {
    const [fromKey, toKey] = dateRangeUrlKeys(key);
    params[fromKey] = ranges?.[key]?.from ?? "";
    params[toKey] = ranges?.[key]?.to ?? "";
  });
  return params;
};

// Second guard: an invalid day yields no bound instead of throwing.
const localDayBoundary = (ymd: string, time: string) => {
  const date = new Date(`${ymd}T${time}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const apiBound = (
  ymd: string | undefined,
  { dateOnly, time }: { dateOnly: boolean; time: string }
) => {
  if (!isYmd(ymd)) return undefined;
  return dateOnly ? ymd : localDayBoundary(ymd, time);
};

// API params: `<key>_from` / `<key>_to`, ISO timestamps for timestamp columns.
export const buildDateRangeApiParams = (ranges?: DateRanges) => {
  const params: Record<string, string> = {};
  DATE_FILTER_KEYS.forEach((key) => {
    const range = ranges?.[key];
    if (!range) return;
    const dateOnly = DATE_ONLY_KEYS.includes(key);
    const [fromKey, toKey] = dateRangeUrlKeys(key);
    const from = apiBound(range.from, { dateOnly, time: "00:00:00" });
    const to = apiBound(range.to, { dateOnly, time: "23:59:59.999" });
    if (from) params[fromKey] = from;
    if (to) params[toKey] = to;
  });
  return params;
};

const formatYmd = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

export const formatDateRangeLabel = (key: DateFilterKey, range: DateRange) => {
  const label = DATE_FILTER_LABELS[key];
  if (range.from && range.to)
    return range.from === range.to
      ? `${label}: ${formatYmd(range.from)}`
      : `${label}: ${formatYmd(range.from)} – ${formatYmd(range.to)}`;
  if (range.from) return `${label}: from ${formatYmd(range.from)}`;
  if (range.to) return `${label}: until ${formatYmd(range.to)}`;
  return label;
};
